import {
    ArrowLeftOutlined, PoweroffOutlined,
} from '@ant-design/icons';
import {
    useMutation, useQuery,
} from '@tanstack/react-query';
import {
    Button, Card, Col, Flex, Grid, Row, Space, theme, Typography,
} from 'antd';
import {
    useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { Helmet } from 'react-helmet';
import {
    Link,
    useParams,
} from 'react-router-dom';

import {
    projectsQueries, scriptsMutations, scriptsQueries,
} from '@/api';
import {
    ConfirmModal, CrossSellsModal, getActionControlStyleVars, ScriptActiveBadge, ScriptForm, ScriptFormRef, ScriptFormSubmitData,
} from '@/components';
import { AppRoute } from '@/constants';
import { useBackToOrList } from '@/hooks';
import { useUIStore } from '@/stores';

export const ScriptEditPage = () => {
    const { id, projectId } = useParams<{ id: string; projectId: string }>();
    const scriptsListPath = projectId ? `/projects/${projectId}` : AppRoute.Scripts;
    const handleBack = useBackToOrList(scriptsListPath);
    const { token } = theme.useToken();
    const formRef = useRef<ScriptFormRef>(null);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [toggleConfirmOpen, setToggleConfirmOpen] = useState(false);
    const [crossSellsModalOpen, setCrossSellsModalOpen] = useState(false);
    const [formData, setFormData] = useState<ScriptFormSubmitData | null>(null);

    const { setPageTitle } = useUIStore();
    const { data: project } = useQuery({
        ...projectsQueries.getOneProject(projectId || ''),
        enabled: !!projectId,
    });

    useEffect(() => {
        setPageTitle(project?.title ?? null);
        return () => setPageTitle(null);
    }, [project?.title, setPageTitle]);

    const { data: script, isLoading, refetch } = useQuery({
        ...scriptsQueries.getOneScript(id || '', projectId),
        refetchOnWindowFocus: false,
    });

    const handleOpenCrossSellsModal = useCallback(() => {
        setCrossSellsModalOpen(true);
    }, []);

    const handleCloseCrossSellsModal = useCallback(() => {
        setCrossSellsModalOpen(false);
    }, []);

    const handleCrossSellsSuccess = useCallback(() => {
        refetch();
    }, [refetch]);
    const updateMutationOptions = scriptsMutations.update(projectId || '', id || '');
    const { mutate: updateScript, isPending } = useMutation(updateMutationOptions);
    const { mutate: bulkAttach } = useMutation(scriptsMutations.bulkAttachCrossSell(projectId || ''));
    const { mutate: bulkDetach } = useMutation(scriptsMutations.bulkDetachCrossSell(projectId || ''));
    const { mutateAsync: bulkUpdateInstruction } = useMutation(scriptsMutations.bulkUpdateInstruction(projectId || ''));
    const toggleMutationOptions = scriptsMutations.toggleActive(projectId || '', id || '');
    const { mutate: toggleScriptActive, isPending: isTogglePending } = useMutation(toggleMutationOptions);

    const handleSubmit = useCallback((data: ScriptFormSubmitData) => {
        setFormData(data);
        setConfirmModalOpen(true);
    }, []);

    const handleConfirm = useCallback(() => {
        if (formData && projectId && id) {
            updateScript(formData, {
                onSuccess: async () => {
                    if (formData.type === 'cross-sale') {
                        if (formData.addToAllScripts) {
                            bulkAttach({ cross_sell_ids: [id] });
                        }
                        if (formData.removeFromAllScripts) {
                            bulkDetach({ cross_sell_ids: [id] });
                        }
                    }

                    if (formData.bulkUpdateInstructions.length > 0) {
                        const results = await Promise.allSettled(
                            formData.bulkUpdateInstructions.map((instr) =>
                                bulkUpdateInstruction({
                                    instruction_name:  instr.name,
                                    instruction_value: instr.value,
                                }),
                            ),
                        );

                        const successCount = results.filter((r) => r.status === 'fulfilled').length;
                        if (successCount > 0) {
                            useUIStore.getState().setNotification({
                                type: 'success',
                                message: `Розділи оновлено в усіх скриптах (${successCount} з ${results.length})`,
                            });
                        }
                    }

                    setConfirmModalOpen(false);
                    setFormData(null);
                    formRef.current?.markSaved();
                    await refetch();
                },
            });
        }
    }, [formData, updateScript, bulkAttach, bulkDetach, bulkUpdateInstruction, refetch, projectId, id]);

    const handleCancel = useCallback(() => {
        handleBack();
    }, [handleBack]);

    const handleModalCancel = useCallback(() => {
        setConfirmModalOpen(false);
        setFormData(null);
    }, []);

    const handleToggleConfirm = useCallback(() => {
        if (projectId && id) {
            toggleScriptActive(undefined, {
                onSuccess: async () => {
                    setToggleConfirmOpen(false);
                    await refetch();
                },
            });
        }
    }, [projectId, id, toggleScriptActive, refetch]);

    const isScriptActive = Boolean(script?.is_active ?? true);

    const screens = Grid.useBreakpoint();
    const isMobileDetailLayout = screens.lg === false;

    const editCardStyles = useMemo(() => {
        if (!isMobileDetailLayout) {
            return undefined;
        }
        return {
            header: {
                paddingTop: 16,
                paddingBottom: 16,
                paddingLeft: 16,
                paddingRight: 16,
                overflow: 'visible',
            },
            title: {
                display: 'block',
                overflow: 'visible',
                whiteSpace: 'normal',
                textOverflow: 'unset',
                width: '100%',
                maxWidth: '100%',
            },
            body: {
                padding: 16,
            },
        };
    }, [isMobileDetailLayout]);

    return (
        <>
            <Helmet>
                <title>Редагувати скрипт</title>
            </Helmet>

            <Row justify="center">
                <Col xs={24} sm={24} md={20} lg={18} xl={16}>
                    <Card
                        className="detail-page-card"
                        styles={editCardStyles}
                        title={
                            <Flex
                                vertical={isMobileDetailLayout}
                                align={isMobileDetailLayout ? 'stretch' : 'center'}
                                justify="space-between"
                                gap="middle"
                                wrap="wrap"
                                style={{ width: '100%' }}
                            >
                                <Flex align="center" gap="middle" style={{ minWidth: 0, flex: isMobileDetailLayout ? undefined : 1 }}>
                                    <Link
                                        to={scriptsListPath}
                                        aria-label="Назад"
                                        className="app-action-control app-action-control--icon"
                                        style={getActionControlStyleVars(token, {
                                            variant: 'text',
                                            bordered: false,
                                            iconOnly: true,
                                        })}
                                    >
                                        <ArrowLeftOutlined style={{ fontSize: 16 }} />
                                    </Link>
                                    <Space align="center" size="middle" wrap>
                                        <Typography.Title
                                            level={4}
                                            ellipsis={false}
                                            style={{ margin: 0, whiteSpace: 'normal', wordBreak: 'break-word' }}
                                        >
                                            <span>
                                                {script?.title || 'Редагувати скрипт'}
                                            </span>
                                        </Typography.Title>
                                        {script && (
                                            <ScriptActiveBadge isActive={Boolean(script.is_active ?? true)} />
                                        )}
                                    </Space>
                                </Flex>
                                <Flex
                                    wrap="wrap"
                                    style={{
                                        marginLeft: isMobileDetailLayout ? 0 : 'auto',
                                        justifyContent: isMobileDetailLayout ? 'flex-start' : undefined,
                                    }}
                                >
                                    <Button
                                        icon={<PoweroffOutlined />}
                                        onClick={() => setToggleConfirmOpen(true)}
                                        disabled={isLoading || !script}
                                        loading={isTogglePending}
                                        style={{ borderColor: token.colorError, backgroundColor: token.colorError, color: token.colorWhite}}
                                    >
                                        {isScriptActive ? 'Вимкнути скрипт' : 'Увімкнути скрипт'}
                                    </Button>
                                </Flex>
                            </Flex>
                        }
                    >
                        <ScriptForm
                            ref={formRef}
                            initData={script || null}
                            onSubmit={handleSubmit}
                            onCancel={handleCancel}
                            loading={isLoading || isPending}
                            cancelTo={projectId && id ? `/projects/${projectId}/scripts/${id}` : scriptsListPath}
                            projectId={projectId}
                            scriptId={id}
                            onOpenCrossSellsModal={handleOpenCrossSellsModal}
                        />
                    </Card>
                </Col>
            </Row>

            <ConfirmModal
                open={confirmModalOpen}
                mode="update"
                entityType="script"
                entityName={formData?.title || script?.title}
                onConfirm={handleConfirm}
                onCancel={handleModalCancel}
                loading={isPending}
            />

            <ConfirmModal
                open={toggleConfirmOpen}
                mode="update"
                entityType="script"
                entityName={script?.title}
                onConfirm={handleToggleConfirm}
                onCancel={() => setToggleConfirmOpen(false)}
                loading={isTogglePending}
                confirmTitle={isScriptActive ? 'Вимкнути скрипт?' : 'Увімкнути скрипт?'}
                confirmMessage={
                    isScriptActive
                        ? 'Скрипт стане неактивним для менеджерів. Продовжити?'
                        : 'Скрипт знову стане активним для менеджерів. Продовжити?'
                }
                confirmOkText={isScriptActive ? 'Вимкнути' : 'Увімкнути'}
            />

            {projectId && id && (
                <CrossSellsModal
                    open={crossSellsModalOpen}
                    projectId={projectId}
                    scriptId={id}
                    currentCrossSells={script?.cross_sales || []}
                    onClose={handleCloseCrossSellsModal}
                    onSuccess={handleCrossSellsSuccess}
                />
            )}
        </>
    );
};
